import * as React from 'react';
import { useEffect, useState } from 'react';
import { Chip, FormControlLabel, Switch, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { sendServerRequest } from '../../server/request';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';
import { URLS } from '../../env/urls';

interface TelegramPaidConfig {
  enabled: boolean;
  chat_id: string;
  bot_username: string;
  bot_token_masked: string;
  has_token: boolean;
  ready: boolean;
}

/** Extrai a mensagem de erro do JSONResponse ({data, hasError, error}). */
function parseError(err: any, fallback: string): string {
  try {
    const txt = err?.target?.responseText;
    if (txt) {
      const j = JSON.parse(txt);
      if (j?.error && typeof j.error === 'string') return j.error;
    }
  } catch (e) {}
  return fallback;
}

/**
 * Config do aviso interno no Telegram quando uma proposta FGTS/CLT é PAGA
 * (mensagem com valor + comissão). O bot é dedicado ao aviso (não é um bot de
 * atendimento) e o token nunca volta cru do servidor — só mascarado.
 */
interface TelegramNotifyUrls { get: string; set: string; test: string }

export function TelegramPaidNotifyPanel() {
  return (
    <TelegramNotifyPanel
      urls={URLS.admin.notifications.telegramPaid}
      title="Aviso de proposta paga (Telegram)"
      description="Envia no Telegram o valor e a comissão de cada proposta FGTS/CLT paga."
    />
  );
}

/** Aviso quando nenhum número de atendimento tem qualidade alta (clientes vão para o melhor disponível). */
export function TelegramAttendanceAlertPanel() {
  return (
    <TelegramNotifyPanel
      urls={URLS.admin.notifications.telegramAttendance}
      title="Aviso de qualidade do atendimento (Telegram)"
      description="Avisa quando nenhum número de atendimento está com qualidade alta e os clientes passam a ser enviados ao melhor disponível (checado a cada 30 min)."
    />
  );
}

function TelegramNotifyPanel({ urls, title, description }: { urls: TelegramNotifyUrls; title: string; description: string }) {
  const [config, setConfig] = useState<TelegramPaidConfig | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [chatId, setChatId] = useState('');
  // Vazio = mantém o token atual; só envia se o admin digitar um novo.
  const [botToken, setBotToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    load();
  }, []);

  function applyServer(data: TelegramPaidConfig) {
    setConfig(data);
    setEnabled(!!data.enabled);
    setChatId(data.chat_id || '');
    setBotToken('');
  }

  function load() {
    setLoading(true);
    sendServerRequest('GET', urls.get)
      .then((e) => {
        applyServer(e.data?.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar a config do aviso.', 'error');
      });
  }

  const dirty = config !== null && (
    enabled !== config.enabled ||
    chatId.trim() !== (config.chat_id || '') ||
    botToken.trim() !== ''
  );

  function save() {
    if (!config) return;
    const body: any = {};
    if (enabled !== config.enabled) body.enabled = enabled;
    if (chatId.trim() !== (config.chat_id || '')) body.chat_id = chatId.trim();
    if (botToken.trim() !== '') body.bot_token = botToken.trim();
    setSaving(true);
    sendServerRequest('POST', urls.set, body)
      .then((e) => {
        applyServer(e.data?.data);
        setSaving(false);
        openGlobalSnack('Config do aviso de proposta paga atualizada.', 'success');
      })
      .catch((err) => {
        setSaving(false);
        openGlobalSnack(parseError(err, 'Não foi possível salvar a config.'), 'error');
      });
  }

  function test() {
    setTesting(true);
    sendServerRequest('POST', urls.test)
      .then((e) => {
        setTesting(false);
        openGlobalSnack(`Mensagem de teste enviada para o chat ${e.data?.data?.to}.`, 'success');
      })
      .catch((err) => {
        setTesting(false);
        openGlobalSnack(parseError(err, 'Falha ao enviar a mensagem de teste.'), 'error');
      });
  }

  if (loading) return <CircularLoading />;
  if (!config) return null;

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>
        {title}{' '}
        {config.ready
          ? <Chip size="small" color="success" label="ativo" />
          : <Chip size="small" label={config.enabled ? 'incompleto' : 'desligado'} />}
      </h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0 }}>
        {description}{' '}
        Crie um bot no <b>@BotFather</b>, cole o token e informe o chat_id de destino
        (dê /start no bot, ou adicione-o ao grupo — id de grupo é negativo).
      </p>
      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label="Habilitado"
      />
      <TextField
        fullWidth
        size="small"
        margin="dense"
        label="Token do bot"
        placeholder={config.has_token ? `Atual: ${config.bot_token_masked} — digite para trocar` : 'Ex.: 123456789:AAF...'}
        value={botToken}
        onChange={(e) => setBotToken(e.target.value)}
        helperText={config.has_token && config.bot_username ? `Bot atual: @${config.bot_username}` : 'Nenhum token salvo'}
      />
      <TextField
        fullWidth
        size="small"
        margin="dense"
        label="chat_id de destino"
        placeholder="Ex.: 123456789 ou -100987654321 (grupo)"
        value={chatId}
        onChange={(e) => setChatId(e.target.value)}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
        <LoadingButton loading={saving} disabled={!dirty} variant="contained" onClick={save}>
          Salvar
        </LoadingButton>
        <LoadingButton
          loading={testing}
          disabled={!config.has_token || !config.chat_id || dirty}
          variant="outlined"
          onClick={test}
          title={dirty ? 'Salve as alterações antes de testar' : 'Envia uma mensagem de teste com a config salva'}
        >
          Testar envio
        </LoadingButton>
      </div>
    </div>
  );
}
